/**
 * CompactSignalCard
 * =================
 *
 * Tiny "scan-friendly" tile for the Compact view of Data Signals.
 * Designed to fit ~6 cards per row at desktop widths and ~120 px
 * tall. Shows only the things you can read at a glance:
 *
 *   - Family icon + signal key (truncated)
 *   - One-glance status icons (high relevance, anomaly)
 *   - A small sparkline (same renderer as the Rows view)
 *   - One-line footer: records · last value
 *
 * Action icons (publish / delete / unsubscribe) and the marketplace
 * provenance pill only reveal on hover so the resting state stays
 * uncluttered.
 */

import React from 'react';
import {
  Box, Card, IconButton, Stack, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Numbers as NumbersIcon,
  Category as CategoryIcon,
  ToggleOn as ToggleOnIcon,
  Schedule as ScheduleIcon,
  TextFields as TextFieldsIcon,
  Tag as TagIcon,
  Place as PlaceIcon,
  Layers as LayersIcon,
  StarOutline as StarOutlineIcon,
  Warning as WarningIcon,
  DeleteOutline as DeleteOutlineIcon,
  Storefront as StorefrontIcon,
  AddBusiness as AddBusinessIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';

import MiniSparkline from './MiniSparkline';
import EventMiniSparkline from './EventMiniSparkline';
import {
  formatLargeNumber, formatNumber,
} from '../../utils/utils';
import { familyOf, hasAnomalies, isEventLike } from './SignalCard';
import { useCanOpenPublishFlow } from '../marketplace/marketplaceUtils';

const FAMILY_META = {
  event:       { icon: BoltIcon,       tone: 'error'     },
  numeric:     { icon: NumbersIcon,    tone: 'primary'   },
  categorical: { icon: CategoryIcon,   tone: 'secondary' },
  binary:      { icon: ToggleOnIcon,   tone: 'info'      },
  time:        { icon: ScheduleIcon,   tone: 'success'   },
  text:        { icon: TextFieldsIcon, tone: 'warning'   },
  id:          { icon: TagIcon,        tone: 'neutral'   },
  spatial:     { icon: PlaceIcon,      tone: 'success'   },
  mixed:       { icon: LayersIcon,     tone: 'neutral'   },
};

const toneColor = (theme, tone) => {
  switch (tone) {
    case 'primary':   return theme.palette.primary.main;
    case 'secondary': return theme.palette.secondary.main;
    case 'success':   return theme.palette.success.main;
    case 'info':      return theme.palette.info.main;
    case 'warning':   return theme.palette.warning.main;
    case 'error':     return theme.palette.error.main;
    default:          return theme.palette.text.secondary;
  }
};

const lastValueOf = (bins) => {
  if (!Array.isArray(bins)) return null;
  for (let i = bins.length - 1; i >= 0; i -= 1) {
    const b = bins[i];
    const c = Number(b?.count) || 0;
    const v = Number(b?.lastValue);
    if (c > 0 && Number.isFinite(v)) return v;
  }
  return null;
};

const formatStat = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  if (Math.abs(v) >= 1000) return formatLargeNumber(v) || formatNumber(v);
  return Number(v).toLocaleString(undefined, {
    maximumFractionDigits: 2, minimumFractionDigits: 0,
  });
};

const CompactSignalCard = ({
  signal, relevance, onDelete, onPublish, onUnsubscribe, onOpen,
  publishedListing,
}) => {
  const theme = useTheme();
  // Publish affordance is capability-gated; published signals keep
  // their manage affordance so sellers can always delist.
  const canOpenPublishFlow = useCanOpenPublishFlow();
  if (!signal) return null;

  const family = familyOf(signal);
  const fam = FAMILY_META[family] || FAMILY_META.mixed;
  const FamIcon = fam.icon;
  const famColor = toneColor(theme, fam.tone);

  const isEvent = isEventLike(signal);

  const records = signal.signal_count || 0;
  const recordsLabel = formatLargeNumber(records) || formatNumber(records);
  const lastVal = formatStat(lastValueOf(signal.bins));

  const relValue = relevance && typeof relevance[signal.key] === 'number'
    ? relevance[signal.key] : null;
  const isHighRel = typeof relValue === 'number' && relValue >= 0.8;
  const anomalous = hasAnomalies(signal);

  const isMarketplace = !!signal.marketplace;
  const isMktUnavailable = isMarketplace
    && signal.marketplace.data_status === 'unavailable';
  const displayKey = signal.display_name
    || signal.marketplace?.original_key || signal.key;

  // Per-action click guard — stops the card's onClick (the "open
  // detail modal" handler) from firing when the user really meant
  // to publish / delete / unsubscribe via the action icons.
  const stop = (fn) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (fn) fn();
  };

  return (
    <Card
      variant="outlined"
      onClick={onOpen ? () => onOpen(signal) : undefined}
      sx={{
        height: '100%',
        minHeight: 124,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.25,
        background: alpha(theme.palette.background.paper, 0.96),
        border: `1px solid ${alpha(
          isMktUnavailable
            ? theme.palette.warning.main
            : theme.palette.divider,
          isMktUnavailable ? 0.4 : 0.18,
        )}`,
        opacity: isMktUnavailable ? 0.85 : 1,
        overflow: 'hidden',
        transition: 'border-color 120ms, box-shadow 120ms',
        position: 'relative',
        cursor: onOpen ? 'pointer' : 'default',
        // Action icons hide until hover; keeps the resting card
        // visually quiet for high-density scanning.
        '& .compact-actions': { opacity: 0, transition: 'opacity 120ms' },
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          boxShadow: theme.shadows[1],
          '& .compact-actions': { opacity: 1 },
        },
      }}
    >
      {/* Top row: family icon + key + status icons + (hover) actions */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ px: 1, pt: 0.75, pb: 0.25, minHeight: 22 }}
      >
        <Box sx={{
          width: 16, height: 16, borderRadius: 0.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(famColor, 0.12), color: famColor, flexShrink: 0,
        }}>
          <FamIcon sx={{ fontSize: 11 }} />
        </Box>
        <Tooltip title={displayKey}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              flex: 1, minWidth: 0,
              fontSize: 11.5, lineHeight: 1.1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {displayKey}
          </Typography>
        </Tooltip>
        {isHighRel && (
          <Tooltip title={`Relevance ${Math.round(relValue * 100)}%`}>
            <StarOutlineIcon sx={{
              fontSize: 12, color: theme.palette.warning.main, flexShrink: 0,
            }} />
          </Tooltip>
        )}
        {anomalous && (
          <Tooltip title="Anomalies detected">
            <WarningIcon sx={{
              fontSize: 12, color: theme.palette.error.main, flexShrink: 0,
            }} />
          </Tooltip>
        )}
        {isMarketplace && (
          <Tooltip
            title={isMktUnavailable
              ? `No data from ${signal.marketplace.seller_label || 'seller'} `
                + 'right now - subscription is active but no signals '
                + 'are available'
              : `Marketplace · ${signal.marketplace.seller_label || 'Seller'}`}
          >
            <Stack direction="row" spacing={0.25} alignItems="center"
                   sx={{ flexShrink: 0 }}>
              <StorefrontIcon sx={{
                fontSize: 11,
                color: isMktUnavailable
                  ? theme.palette.warning.main
                  : theme.palette.primary.main,
              }} />
              {isMktUnavailable && (
                <WarningIcon sx={{
                  fontSize: 11, color: theme.palette.warning.main,
                }} />
              )}
              {!isMktUnavailable && signal.marketplace.verified && (
                <VerifiedIcon sx={{
                  fontSize: 11, color: theme.palette.primary.main,
                }} />
              )}
            </Stack>
          </Tooltip>
        )}
      </Stack>

      {/* Sparkline takes the visual centre */}
      <Box sx={{ flex: 1, px: 0.5, minHeight: 44 }}>
        {isEvent ? (
          <EventMiniSparkline points={signal.points} height={44} />
        ) : (
          <MiniSparkline
            bins={signal.bins}
            height={44}
            color={isHighRel ? theme.palette.warning.main : theme.palette.primary.main}
          />
        )}
      </Box>

      {/* Footer: records · last value · (relevance % when present) */}
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{
          px: 1, pb: 0.6, pt: 0.25,
          fontSize: 10, color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {recordsLabel}
        </Box>
        {lastVal !== null && (
          <>
            <Box component="span">·</Box>
            <Tooltip title={signal.unit ? `Last value (${signal.unit})` : 'Last value'}>
              <Box component="span">
                {signal.unit ? `${lastVal} ${signal.unit}` : lastVal}
              </Box>
            </Tooltip>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {typeof relValue === 'number' && (
          <Box component="span" sx={{
            fontWeight: 700,
            color: isHighRel
              ? theme.palette.warning.main
              : theme.palette.primary.main,
          }}>
            {Math.round(relValue * 100)}%
          </Box>
        )}
      </Stack>
      {/* Hover-only action cluster, anchored bottom-right. Each icon
          calls e.stopPropagation() so clicks here don't bubble up
          to the Card's onClick (which opens the detail modal). */}
      <Box
        className="compact-actions"
        sx={{
          position: 'absolute',
          bottom: 2, right: 2,
          display: 'flex',
          bgcolor: alpha(theme.palette.background.paper, 0.95),
          borderRadius: 1,
          zIndex: 1,
        }}
      >
        {!isMarketplace && onPublish
          && (publishedListing || canOpenPublishFlow) && (
          <Tooltip title={publishedListing
              ? `Published · ${publishedListing.subscribers_count || 0} subscriber${(publishedListing.subscribers_count || 0) === 1 ? '' : 's'}`
              : 'Put on Marketplace'}>
            <IconButton
              size="small"
              onClick={stop(() => onPublish(signal, publishedListing))}
              sx={{
                p: 0.25,
                color: publishedListing
                  ? theme.palette.success.main
                  : 'text.secondary',
                bgcolor: publishedListing
                  ? alpha(theme.palette.success.main, 0.12)
                  : 'transparent',
                '&:hover': {
                  color: publishedListing
                    ? theme.palette.success.dark
                    : 'primary.main',
                },
              }}
            >
              {publishedListing
                ? <StorefrontIcon sx={{ fontSize: 12 }} />
                : <AddBusinessIcon sx={{ fontSize: 12 }} />}
            </IconButton>
          </Tooltip>
        )}
        {isMarketplace && onUnsubscribe && (
          <Tooltip title="Unsubscribe">
            <IconButton
              size="small"
              onClick={stop(() => onUnsubscribe(signal.marketplace.subscription_id))}
              sx={{ p: 0.25, color: theme.palette.primary.main }}
            >
              <CancelIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        )}
        {!isMarketplace && onDelete && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={stop(() => onDelete(signal.key))}
              sx={{ p: 0.25, color: 'text.secondary',
                    '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
};

export default CompactSignalCard;
